/**
 * Authentication context provider
 * Manages user authentication state and team selection
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode, ReactElement } from 'react';

import { ProfileCompletion } from '../components/auth/ProfileCompletion';
import { api, UnauthorizedError } from '../services/api';
import { getCurrentUser, fetchAuthSession, signOut as authSignOut } from '../services/auth';
import type { UserProfile } from '../types';

// ============================================================================
// TYPE DEFINITIONS (aligned with backend)
// ============================================================================

export interface Team {
  teamId: string;
  name: string;
  memberCount: number;
  role: 'admin' | 'member';
  createdAt: string;
  updatedAt: string;
}

export interface User {
  userId: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  name?: string;
  profileComplete?: boolean;
}

export interface AuthContextValue {
  // User state
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresProfileCompletion: boolean;

  // Team state
  teams: Team[];
  currentTeam: Team | null;
  setCurrentTeam: (team: Team | null) => void;

  // Auth actions
  checkAuth: () => Promise<void>;
  signOut: () => Promise<void>;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ============================================================================
// CONSTANTS
// ============================================================================

const CURRENT_TEAM_KEY = 'adverdict_current_team_id';

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [currentTeam, setCurrentTeamState] = useState<Team | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [requiresProfileCompletion, setRequiresProfileCompletion] = useState(false);

  /**
   * Fetch user profile from the API
   */
  const fetchUserProfile = useCallback(async (): Promise<UserProfile | null> => {
    try {
      const profile = await api.get<UserProfile>('/users/me');
      return profile;
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        throw error;
      }
      console.error('Failed to fetch user profile:', error);
      return null;
    }
  }, []);

  /**
   * Fetch user's teams from the API
   */
  const fetchTeams = useCallback(async (): Promise<Team[]> => {
    try {
      const response = await api.get<Team[]>('/teams');
      return response;
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        // User is not authenticated
        throw error;
      }
      console.error('Failed to fetch teams:', error);
      return [];
    }
  }, []);

  /**
   * Check authentication status and load user data
   */
  const checkAuth = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      // Check if user is authenticated
      const [currentUser, session] = await Promise.all([getCurrentUser(), fetchAuthSession()]);

      if (!currentUser || !session) {
        // Not authenticated
        setUser(null);
        setTeams([]);
        setCurrentTeamState(null);
        setIsAuthenticated(false);
        setRequiresProfileCompletion(false);
        setIsLoading(false);
        return;
      }

      // Set user and mark as authenticated
      setUser(currentUser);
      setIsAuthenticated(true);

      // Fetch user profile to check if profile is complete
      const profile = await fetchUserProfile();
      if (profile && !profile.profileComplete) {
        // User needs to complete their profile
        setRequiresProfileCompletion(true);
        // Update user state with profile data
        setUser({
          ...currentUser,
          firstName: profile.firstName,
          lastName: profile.lastName,
          profileComplete: profile.profileComplete,
        });
        setIsLoading(false);
        return;
      }

      // Profile is complete, update user with profile data
      if (profile) {
        setUser({
          ...currentUser,
          firstName: profile.firstName,
          lastName: profile.lastName,
          profileComplete: profile.profileComplete,
        });
      }
      setRequiresProfileCompletion(false);

      // Fetch user's teams
      const userTeams = await fetchTeams();
      setTeams(userTeams);

      // Restore current team from localStorage or use first team
      const savedTeamId = localStorage.getItem(CURRENT_TEAM_KEY);
      if (savedTeamId) {
        const savedTeam = userTeams.find((t) => t.teamId === savedTeamId);
        if (savedTeam) {
          setCurrentTeamState(savedTeam);
        } else {
          // Saved team not found, use first team
          setCurrentTeamState(userTeams[0] || null);
        }
      } else {
        // No saved team, use first team
        setCurrentTeamState(userTeams[0] || null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setTeams([]);
      setCurrentTeamState(null);
      setIsAuthenticated(false);
      setRequiresProfileCompletion(false);
    } finally {
      setIsLoading(false);
    }
  }, [fetchTeams, fetchUserProfile]);

  /**
   * Set current team and persist to localStorage
   */
  const setCurrentTeam = useCallback((team: Team | null): void => {
    setCurrentTeamState(team);
    if (team) {
      localStorage.setItem(CURRENT_TEAM_KEY, team.teamId);
    } else {
      localStorage.removeItem(CURRENT_TEAM_KEY);
    }
  }, []);

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async (): Promise<void> => {
    try {
      await authSignOut();
      setUser(null);
      setTeams([]);
      setCurrentTeamState(null);
      setIsAuthenticated(false);
      setRequiresProfileCompletion(false);
      localStorage.removeItem(CURRENT_TEAM_KEY);
    } catch (error) {
      console.error('Sign out failed:', error);
      throw error;
    }
  }, []);

  /**
   * Handle profile completion
   */
  const handleProfileComplete = useCallback(async (profile: UserProfile) => {
    setUser((prevUser) => prevUser ? {
      ...prevUser,
      firstName: profile.firstName,
      lastName: profile.lastName,
      profileComplete: profile.profileComplete,
    } : null);
    setRequiresProfileCompletion(false);

    // Now fetch teams since profile is complete
    const userTeams = await fetchTeams();
    setTeams(userTeams);

    // Set current team
    const savedTeamId = localStorage.getItem(CURRENT_TEAM_KEY);
    if (savedTeamId) {
      const savedTeam = userTeams.find((t) => t.teamId === savedTeamId);
      setCurrentTeamState(savedTeam || userTeams[0] || null);
    } else {
      setCurrentTeamState(userTeams[0] || null);
    }
  }, [fetchTeams]);

  /**
   * Check authentication on mount
   */
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Context value
  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading,
    requiresProfileCompletion,
    teams,
    currentTeam,
    setCurrentTeam,
    checkAuth,
    signOut,
  };

  // Show profile completion screen if required
  if (isAuthenticated && requiresProfileCompletion && user) {
    return (
      <AuthContext.Provider value={value}>
        <ProfileCompletion
          email={user.email}
          onComplete={handleProfileComplete}
        />
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
